import { AnimatePresence, motion } from 'framer-motion';
import { FC, HTMLAttributes, ReactNode } from 'react';
import { toast, Toast, useToaster } from 'react-hot-toast';
import { FiAlertCircle, FiAlertTriangle, FiCheck, FiX } from 'react-icons/fi';
import { IconButton } from '../../components/shared/button/IconButton';
import { TSCard } from '../../components/shared/card/Card';
import { Icon } from '../../components/shared/icons/Icon';

interface ToastServiceProps extends HTMLAttributes<HTMLDivElement> {}

export const createSuccessToast = (message: string) => {
  toast.success(message, {
    position: 'top-right'
  });
};

export const createErrorToast = (message: string) => {
  toast.error(message, {
    position: 'top-right'
  });
};

export const ToastService: FC<ToastServiceProps> = ({ children }) => {
  const { toasts, handlers } = useToaster();

  interface ToastContentsProps {
    toast: Toast;
  }

  const ErrorToast: FC<ToastContentsProps> = ({ toast }) => {
    return (
      <>
        <p className="text-red-400 font-bold text-lg leading-tight capitalize">{toast.type}</p>
        <p className="text-md leading-tight">{toast.message as ReactNode}</p>
      </>
    );
  };

  const NotificationToast: FC<ToastContentsProps> = ({ toast }) => {
    return <p className="text-md leading-tight">{toast.message as ReactNode}</p>;
  };

  const SuccessToast: FC<ToastContentsProps> = ({ toast }) => {
    return (
      <>
        <p className="text-primary-500 font-bold text-lg leading-tight capitalize">{toast.type}</p>
        <p className="text-md leading-tight">{toast.message as ReactNode}</p>
      </>
    );
  };

  const getContentForToast = (toast: Toast) => {
    switch (toast.type) {
      case 'error':
        return <ErrorToast toast={toast} />;
      case 'success':
        return <SuccessToast toast={toast} />;
      default:
        return <NotificationToast toast={toast} />;
    }
  };

  const calcToastPosition = (toast: Toast): string => {
    let toastPosition = 'sm:self-center self-center w-full';

    if (toast.position?.includes('left')) {
      toastPosition = 'sm:self-start self-center w-full';
    } else if (toast.position?.includes('right')) {
      toastPosition = 'sm:self-end self-center w-full';
    }

    return toastPosition;
  };

  const calcToastIcon = (toast: Toast) => {
    if (toast.type === 'success') return <Icon Icon={FiCheck} theme="emerald" />;
    if (toast.type === 'error') return <Icon Icon={FiAlertTriangle} theme="red" />;
    return <Icon Icon={FiAlertCircle} theme="amber" />;
  };

  return (
    <>
      <div className="fixed inset-0 w-full flex mt-[75px] px-4 py-6 pointer-events-none md:p-6 z-[10000020] items-end md:items-start">
        <div className="w-full flex flex-col space-y-4">
          <AnimatePresence>
            {toasts.map((localToast: Toast) => {
              return (
                <motion.div
                  layout
                  onHoverStart={handlers.startPause}
                  onHoverEnd={handlers.endPause}
                  key={localToast.id}
                  initial={{ opacity: 0, top: -150 }}
                  animate={{ opacity: 1, top: 0 }}
                  exit={{ opacity: 0, top: -150 }}
                  className={`min-w-[300px] max-w-full md:max-w-xl ${calcToastPosition(localToast)}`}
                >
                  <TSCard className="p-4 z-20 flex flex-row gap-4 items-center pointer-events-auto">
                    {calcToastIcon(localToast)}
                    <div className="flex-1 flex flex-col leading-3 w-full break-words">
                      {getContentForToast(localToast)}
                    </div>
                    <IconButton label="Clear notifications" Icon={FiX} onClick={() => toast.remove(localToast.id)} />
                  </TSCard>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
      {children}
    </>
  );
};
